import { useState } from 'react';
import { useToggleControls } from '.';
import Toast from '@zeptonow/zui/Toast';
import Modal from '@zeptonow/zui/Modal';
import CircularProgress from '@zeptonow/zui/CircularProgress';
import Drawer from '@zeptonow/zui/Drawer';
import Typography from '@zeptonow/zui/Typography';
import {
  toastAlertClass,
  toastDescriptionClass,
  toastIconClass,
  toastMessageClass,
} from '../utilities/toastMapping';

import {
  DisplayContainer,
  ContainerDataInterface,
  DisplayContainerHookInterface,
  ToastContainerDataInterface,
  ModalContainerDataInterface,
  CircularProgressContainerDataInterface,
  SideDrawerContainerDataInterface,
} from './types';
import { generateImage } from '../utilities/utils';

const useDisplayContainer: DisplayContainerHookInterface = (props) => {
  const containerType = props.type;
  const [visibility, showContainer, hideContainer] = useToggleControls();
  const [data, setData] =
    useState<ContainerDataInterface[DisplayContainer]>(null);

  const handleShowContainer = (
    data: CustomEvent<ContainerDataInterface[DisplayContainer]>
  ) => {
    setData(data.detail);
    showContainer();
  };

  const handleHideContainer = () => {
    hideContainer();
    containerType === DisplayContainer.TOAST
      ? setTimeout(() => {
          setData(null);
        }, 100)
      : setData(null);
  };

  const ContainerComponent = () => {
    if (containerType === DisplayContainer.TOAST) {
      const { severity, message, description, autoHideDuration } =
        (data as ToastContainerDataInterface) ?? {};

      if (
        typeof visibility !== 'undefined' &&
        typeof severity !== 'undefined'
      ) {
        return (
          <Toast
            isOpen={visibility}
            handleClose={handleHideContainer}
            severity={severity}
            autoHideDuration={autoHideDuration}
            alertClassName={`${toastAlertClass[severity]} !border-[1.5px] !pb-3 !rounded-lg`}
            message={
              <Typography
                className={`${toastMessageClass[severity]} !text-base`}
              >
                {' '}
                {message}{' '}
              </Typography>
            }
            description={
              <Typography
                className={`${toastDescriptionClass[severity]} !text-sm`}
              >
                {' '}
                {description}{' '}
              </Typography>
            }
            icon={generateImage({
              name: `${severity}-icon`,
              alt: 'toast icon',
              className: '',
            })}
            action={
              <div
                className={`hover:cursor-pointer rounded-full ${toastIconClass[severity]}`}
                onClick={handleHideContainer}
              >
                {generateImage({
                  name: `${severity}-cross`,
                  alt: 'cross icon',
                  className: '!m-3 !p-0',
                })}
              </div>
            }
          />
        );
      }
      return null;
    } else if (containerType === DisplayContainer.MODAL) {
      const {
        className,
        title,
        content,
        actions,
        fullWidth = true,
        disableBackDropClick,
        disableEscapeKeyDown,
      } = (data as ModalContainerDataInterface) ?? {};
      const { Title, Content, Actions } = Modal;
      return (
        <Modal
          fullWidth={fullWidth}
          maxWidth="lg"
          open={visibility}
          onClose={(_, reason: string) => {
            if (reason === 'backdropClick') {
              if (!disableBackDropClick) {
                handleHideContainer();
              }
            } else {
              handleHideContainer();
            }
          }}
          className={className}
          disableEscapeKeyDown={disableEscapeKeyDown}
        >
          {title ? <Title> {title} </Title> : null}
          {content ? <Content> {content} </Content> : null}
          {actions ? <Actions> {actions} </Actions> : null}
        </Modal>
      );
    } else if (containerType === DisplayContainer.CIRCULAR_PROGRESS) {
      const { color, isBackdropVisible } =
        (data as CircularProgressContainerDataInterface) ?? {};
      return (
        <CircularProgress
          isBackdropVisible={isBackdropVisible}
          color={color}
          isOpen={visibility}
        />
      );
    } else if (containerType === DisplayContainer.SIDE_DRAWER) {
      const { className, content } =
        (data as SideDrawerContainerDataInterface) ?? {};
      return (
        <Drawer
          open={visibility}
          onClose={handleHideContainer}
          className={className}
          anchor="right"
        >
          {content}
        </Drawer>
      );
    } else return null;
  };

  return [
    visibility,
    handleShowContainer,
    handleHideContainer,
    ContainerComponent,
  ];
};

export default useDisplayContainer;
